export type ShiftStatus = "OPEN" | "FILLED" | "COMPLETED" | "CANCELLED" | "NO_SHOW";

export type ShiftRole =
  // Front of House
  | "SERVER"
  | "HEAD_SERVER_CAPTAIN"
  | "HOST_HOSTESS"
  | "BUSSER"
  | "FOOD_RUNNER"
  | "EXPEDITOR"
  // Bar
  | "BARTENDER"
  | "BARBACK"
  | "SOMMELIER"
  | "COCKTAIL_SERVER"
  // Kitchen / Back of House
  | "EXECUTIVE_CHEF"
  | "SOUS_CHEF"
  | "CHEF_DE_PARTIE"
  | "LINE_COOK"
  | "PREP_COOK"
  | "PASTRY_CHEF_BAKER"
  | "KITCHEN_HELPER_PORTER"
  | "DISHWASHER"
  // Events & Catering
  | "EVENT_STAFF"
  | "CATERING_CREW"
  | "CATERING_CAPTAIN"
  | "BANQUET_SERVER"
  | "BUFFET_ATTENDANT"
  | "COFFEE_TEA_ATTENDANT"
  // Guest Services & Support
  | "COAT_CHECK_ATTENDANT"
  | "WASHROOM_ATTENDANT"
  | "GREETER_CONCIERGE"
  | "PARKING_VALET"
  // Venue Operations
  | "SECURITY_CROWD_CONTROL"
  | "EVENT_SETUP_TEARDOWN"
  | "AV_EVENT_TECH"
  // Specialty
  | "PRIVATE_CHEF"
  | "PERSONAL_BUTLER"
  | "HALAL_KOSHER_COOK"
  | "BARISTA"
  // Legacy generic — still rendered for historical rows but not selectable in
  // the post-shift form's grouped picker.
  | "COOK"
  | "HOST";

/** Roles grouped by category, in the order shown in the post-shift form's
 * accordion picker. Excludes COOK / HOST (legacy generic values kept for
 * back-compat with existing rows but not offered to new posters). */
export const ROLE_GROUPS: Array<{ label: string; roles: ShiftRole[] }> = [
  {
    label: "Front of House",
    roles: ["SERVER", "HEAD_SERVER_CAPTAIN", "HOST_HOSTESS", "BUSSER", "FOOD_RUNNER", "EXPEDITOR"],
  },
  {
    label: "Bar",
    roles: ["BARTENDER", "BARBACK", "SOMMELIER", "COCKTAIL_SERVER"],
  },
  {
    label: "Kitchen / Back of House",
    roles: [
      "EXECUTIVE_CHEF",
      "SOUS_CHEF",
      "CHEF_DE_PARTIE",
      "LINE_COOK",
      "PREP_COOK",
      "PASTRY_CHEF_BAKER",
      "KITCHEN_HELPER_PORTER",
      "DISHWASHER",
    ],
  },
  {
    label: "Events & Catering",
    roles: [
      "EVENT_STAFF",
      "CATERING_CREW",
      "CATERING_CAPTAIN",
      "BANQUET_SERVER",
      "BUFFET_ATTENDANT",
      "COFFEE_TEA_ATTENDANT",
    ],
  },
  {
    label: "Guest Services & Support",
    roles: ["COAT_CHECK_ATTENDANT", "WASHROOM_ATTENDANT", "GREETER_CONCIERGE", "PARKING_VALET"],
  },
  {
    label: "Venue Operations",
    roles: ["SECURITY_CROWD_CONTROL", "EVENT_SETUP_TEARDOWN", "AV_EVENT_TECH"],
  },
  {
    label: "Specialty",
    roles: ["PRIVATE_CHEF", "PERSONAL_BUTLER", "HALAL_KOSHER_COOK", "BARISTA"],
  },
];

export const SHIFT_ROLES: ShiftRole[] = [
  ...ROLE_GROUPS.flatMap((g) => g.roles),
  "COOK",
  "HOST",
];

export const ROLE_LABELS: Record<ShiftRole, string> = {
  // Front of House
  SERVER: "Server",
  HEAD_SERVER_CAPTAIN: "Head Server / Captain",
  HOST_HOSTESS: "Host / Hostess",
  BUSSER: "Busser",
  FOOD_RUNNER: "Food Runner",
  EXPEDITOR: "Expeditor",
  // Bar
  BARTENDER: "Bartender",
  BARBACK: "Barback",
  SOMMELIER: "Sommelier",
  COCKTAIL_SERVER: "Cocktail Server",
  // Kitchen
  EXECUTIVE_CHEF: "Executive Chef",
  SOUS_CHEF: "Sous Chef",
  CHEF_DE_PARTIE: "Chef de Partie",
  LINE_COOK: "Line Cook",
  PREP_COOK: "Prep Cook",
  PASTRY_CHEF_BAKER: "Pastry Chef / Baker",
  KITCHEN_HELPER_PORTER: "Kitchen Helper / Porter",
  DISHWASHER: "Dishwasher",
  // Events & Catering
  EVENT_STAFF: "Event Staff (Convention & Banquet)",
  CATERING_CREW: "Catering Crew (Off-site & Mobile)",
  CATERING_CAPTAIN: "Catering Captain",
  BANQUET_SERVER: "Banquet Server",
  BUFFET_ATTENDANT: "Buffet Attendant",
  COFFEE_TEA_ATTENDANT: "Coffee / Tea Service Attendant",
  // Guest Services & Support
  COAT_CHECK_ATTENDANT: "Coat Check Attendant",
  WASHROOM_ATTENDANT: "Washroom Attendant",
  GREETER_CONCIERGE: "Greeter / Concierge",
  PARKING_VALET: "Parking / Valet Attendant",
  // Venue Operations
  SECURITY_CROWD_CONTROL: "Security / Crowd Control",
  EVENT_SETUP_TEARDOWN: "Event Setup & Teardown Crew",
  AV_EVENT_TECH: "AV / Event Tech Support",
  // Specialty
  PRIVATE_CHEF: "Private Chef",
  PERSONAL_BUTLER: "Personal Butler / Table Captain",
  HALAL_KOSHER_COOK: "Halal / Kosher Certified Cook",
  BARISTA: "Barista",
  // Legacy
  COOK: "Cook",
  HOST: "Host",
};

export interface Shift {
  id: string;
  venue_id: string;
  venue_name: string;
  venue_address: string;
  venue_rating_avg: number | null;
  /** Optional human-readable name for the event/shift, e.g. "Saturday Brunch".
   * Combined with venue_name in the worker-facing UI. Null on legacy postings
   * created before the event-name field shipped. */
  event_name: string | null;
  role: ShiftRole;
  date: string;
  start_time: string;
  end_time: string;
  pay_rate: string;
  dress_code: string | null;
  notes: string | null;
  status: ShiftStatus;
  confirmed_worker_id: string | null;
  paid: boolean;
  invoiced: boolean;
  is_urgent: boolean;
  /** Premium % the venue applied on top of the base pay_rate. Null when not urgent. */
  urgency_premium_percent: number | null;
  end_date: string | null;
  category: ShiftCategory | null;
  transportation_offered: boolean;
  transportation_notes: string | null;
  required_certifications: string[];
  /** True when seeded by `scripts/seed_demo.py`. Real venue postings are always
   * false — the field is never settable from the API. */
  is_demo: boolean;
  pending_applicant_count: number | null;
  /** Total slots in the parent posting (excluding cancellations). Defaults
   * to 1 for legacy single-slot shifts. */
  posting_slots_total: number;
  /** Slots in the parent posting that have a worker assigned (FILLED +
   * COMPLETED + NO_SHOW). */
  posting_slots_filled: number;
  created_at: string;
}

export type ShiftCategory =
  | "RESTAURANT_DINING"
  | "BAR_NIGHTCLUB"
  | "EVENT"
  | "CATERING"
  | "HOTEL"
  | "OTHER";

export const SHIFT_CATEGORIES: ShiftCategory[] = [
  "RESTAURANT_DINING",
  "BAR_NIGHTCLUB",
  "EVENT",
  "CATERING",
  "HOTEL",
  "OTHER",
];

export const CATEGORY_LABELS: Record<ShiftCategory, string> = {
  RESTAURANT_DINING: "Restaurant / dining",
  BAR_NIGHTCLUB: "Bar / nightclub",
  EVENT: "Event",
  CATERING: "Catering",
  HOTEL: "Hotel",
  OTHER: "Other",
};

import { CERT_CATEGORIES, type CertCategory } from "@/shared/lib/certCategories";

/**
 * Allow-listed certifications a venue can require on a posting. Same
 * list workers pick from when categorizing their own uploads — sourced
 * from `shared/lib/certCategories.ts`.
 */
export const REQUIRED_CERTIFICATION_OPTIONS = CERT_CATEGORIES;

export type RequiredCertification = CertCategory;

export interface ShiftListResponse {
  shifts: Shift[];
  total: number | null;
}

export interface CreateShiftPayload {
  role: ShiftRole;
  date: string;
  end_date?: string | null;
  start_time: string;
  end_time: string;
  pay_rate: string;
  dress_code?: string;
  notes?: string;
}

export type UpdateShiftPayload = Partial<CreateShiftPayload> & {
  category?: ShiftCategory | null;
  event_name?: string | null;
  is_urgent?: boolean;
  /** 0-200; only meaningful when is_urgent. Server clears when is_urgent toggles off. */
  urgency_premium_percent?: number | null;
  transportation_offered?: boolean;
  transportation_notes?: string | null;
  required_certifications?: string[];
};

export interface CreatePostingPayload extends CreateShiftPayload {
  slots_requested: number;
  is_urgent?: boolean;
  /** 0-200; only meaningful when is_urgent. Server fills in default if omitted. */
  urgency_premium_percent?: number;
  category?: ShiftCategory;
  event_name?: string;
  transportation_offered?: boolean;
  transportation_notes?: string;
  required_certifications?: string[];
}

export interface Posting {
  id: string;
  venue_id: string;
  role: ShiftRole;
  date: string;
  start_time: string;
  end_time: string;
  pay_rate: string;
  dress_code: string | null;
  notes: string | null;
  slots_requested: number;
  slots_filled: number;
  slots_open: number;
  required_certifications: string[];
  is_demo: boolean;
  pending_applicant_count: number;
  created_at: string;
}

export interface ShiftFilters {
  role?: ShiftRole;
  date_from?: string;
  date_to?: string;
  min_pay?: string;
  city?: string;
  available_only?: boolean;
  urgent_only?: boolean;
}
